// Admission Letter for The Eon Academy — Campus 6
import React, { useEffect, useState } from "react";
import { AdmissionData } from "../admission-list";
import { getAllFeeType } from "../../../../service/fees";
import { getDiscountByEnrollment } from "../../../../service/discount";

interface EonAdmissionLetterContentProps {
  admissionData: AdmissionData;
  logoUrl: string;
  schoolName: string;
  schoolAddress: string;
  formattedDate: string;
}

const EonAdmissionLetterContent: React.FC<EonAdmissionLetterContentProps> = ({
  admissionData,
  logoUrl,
  schoolName,
  schoolAddress,
  formattedDate,
}) => {
  const addressText =
    schoolAddress && schoolAddress.trim().length > 0
      ? schoolAddress
      : "Plot# 54, Block-3, Bahadurabad, Karachi. Ph:03094440000";

  const studentFullName = `${admissionData.firstName || ""} ${
    admissionData.lastName || ""
  }`.trim();
  const appliedClassName = admissionData.appliedClass?.name || "";

  // Tuition Fee isn't part of admissionData -- the class's monthly Tuition
  // Fee amount has to be looked up separately from Fee Types. A brand-new
  // admission has no enrollment yet and no real Tuition discount recorded
  // anywhere (the only discount that early is on the one-time Admission
  // Fee invoice, which is unrelated money -- reusing that against Tuition
  // caused a real live bug: a full Admission Fee waiver drove the shown
  // Tuition amount negative/zero). Once the admission has converted to an
  // enrollment, a real per-student Tuition discount (the same Discount
  // record Collect Fees reads) may exist and is applied here too, exactly
  // like Collect Fees shows it (Fees - Profile/Manual Discount = Amount).
  const [tuitionFeeAmount, setTuitionFeeAmount] = useState<number | null>(null);
  const [tuitionDiscount, setTuitionDiscount] = useState<{
    discount: number;
    discountType: string;
  } | null>(null);

  useEffect(() => {
    const classId = admissionData.appliedClass?._id;
    const sessionId = admissionData.session?._id;
    const systemId = admissionData.system?._id;
    if (!classId || !sessionId || !systemId) {
      setTuitionFeeAmount(null);
      return;
    }
    let cancelled = false;
    getAllFeeType({
      session: sessionId,
      system: systemId,
      classes: classId,
      name: "Tuition Fee",
      status: "active",
      limit: 1,
    })
      .then((res) => {
        if (cancelled) return;
        setTuitionFeeAmount(res?.data?.[0]?.amount ?? null);
      })
      .catch(() => {
        if (!cancelled) setTuitionFeeAmount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    admissionData.appliedClass?._id,
    admissionData.session?._id,
    admissionData.system?._id,
  ]);

  useEffect(() => {
    const enrollmentId = admissionData.enrollment;
    if (!enrollmentId) {
      setTuitionDiscount(null);
      return;
    }
    let cancelled = false;
    getDiscountByEnrollment(enrollmentId)
      .then((res) => {
        if (!cancelled) setTuitionDiscount(res?.data?.discount ?? null);
      })
      .catch(() => {
        if (!cancelled) setTuitionDiscount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [admissionData.enrollment]);

  const netTuitionFeeAmount = (() => {
    if (tuitionFeeAmount == null) return null;
    if (!tuitionDiscount || !tuitionDiscount.discount) return tuitionFeeAmount;
    const d = Number(tuitionDiscount.discount) || 0;
    if (d <= 0) return tuitionFeeAmount;
    if (tuitionDiscount.discountType === "amount") {
      return Math.max(0, tuitionFeeAmount - d);
    }
    if (tuitionDiscount.discountType === "percentage") {
      return Math.max(0, tuitionFeeAmount * (1 - d / 100));
    }
    return tuitionFeeAmount;
  })();

  return (
    <div
      className="admission-letter printable-content"
      id="admission-letter-content"
      style={{
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: "#1a1a1a",
        lineHeight: 1.5,
        fontSize: "13.5px",
      }}
    >
      {/* Header */}
      <div className="mb-3">
        <div
          className="d-flex align-items-center justify-content-center mb-2"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "18px",
          }}
        >
          <img
            src={logoUrl || "/assets/img/icons/logo.png"}
            alt="The Eon Academy"
            className="img-fluid flex-shrink-0"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "contain",
            }}
          />
          <div
            className="flex-grow-1 text-center"
            style={{ maxWidth: "620px" }}
          >
            <h1
              className="fw-bold mb-1"
              style={{
                fontFamily: "'Balthazar', Georgia, 'Times New Roman', serif",
                fontSize: "38px",
                fontWeight: 700,
                color: "#244061",
                letterSpacing: "2.5px",
                lineHeight: "1.1",
                margin: "0 0 6px 0",
              }}
            >
              THE EON ACADEMY
            </h1>
            <div
              style={{
                backgroundColor: "#0F243E",
                color: "#ffffff",
                padding: "4px 16px",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.3px",
                borderRadius: "2px",
                display: "block",
              }}
            >
              Plot# 54, Block-3, Bahadurabad, Karachi. Ph:03094440000
            </div>
          </div>
        </div>

        <div className="text-center my-3">
          <h2
            className="mb-0 fw-bold"
            style={{
              fontSize: "21px",
              color: "#000000",
              letterSpacing: "1.5px",
            }}
          >
            ADMISSION LETTER
          </h2>
        </div>
      </div>

      {/* Date */}
      <div className="mb-3 text-end">
        <span className="fw-semibold">
          Dated:{" "}
          <span className="text-decoration-underline">
            {formattedDate || "________________"}
          </span>
        </span>
      </div>

      {/* Salutation */}
      <div className="mb-2">
        <strong style={{ fontSize: "14px" }}>Dear Parents,</strong>
      </div>

      {/* Congratulations Section */}
      <div className="mb-3">
        <p className="mb-2" style={{ textAlign: "justify" }}>
          <strong>Congratulations!</strong> We are pleased to inform you that your child{" "}
          <strong className="text-decoration-underline text-dark">
            {studentFullName || "_________________________________"}
          </strong>{" "}
          has successfully passed the assessment for admission in{" "}
          <strong className="text-decoration-underline text-dark">
            {appliedClassName || "___________"}
          </strong>
          .
        </p>
        <p className="mb-0">
          Please follow the steps below to proceed with the admission process:
        </p>
      </div>

      {/* Step 1: Admission Form Submission */}
      <div className="mb-3 p-3 bg-light rounded border">
        <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
          1. Admission Form Submission
        </h6>
        <p className="mb-1">
          Kindly click the link below then fill in and submit the admission form.
        </p>
        <div className="mt-1">
          <span className="fw-semibold me-2">Admission form link:</span>
          <a
            href="https://forms.gle/TvTQdJjXgWc7HCYB6"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary fw-semibold text-break"
            style={{ textDecoration: "underline" }}
          >
            https://forms.gle/TvTQdJjXgWc7HCYB6
          </a>
        </div>
      </div>

      {/* Step 2: Fee Payment */}
      <div className="mb-3 p-3 bg-light rounded border">
        <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
          2. Fee Payment
        </h6>
        <p className="mb-2">
          You can pay the total payable fee as per the given fee structure either by
          cash at school or bank deposit in:
        </p>
        <div
          className="p-2 mb-2 bg-white rounded border"
          style={{ fontSize: "13px" }}
        >
          <div className="row g-1">
            <div className="col-sm-4">
              <span className="text-muted">Bank:</span>{" "}
              <strong>Bank Islami</strong>
            </div>
            <div className="col-sm-8">
              <span className="text-muted">Account title:</span>{" "}
              <strong>The Eon Academy</strong>
            </div>
            <div className="col-12 mt-1">
              <span className="text-muted">IBAN:</span>{" "}
              <strong className="font-monospace">
                PK16 BKIP 0105 1005 2884 0001
              </strong>
            </div>
          </div>
        </div>
        <ul className="mb-0 ps-3 small text-secondary" style={{ lineHeight: 1.6 }}>
          <li>
            In case of Bank deposit, please send the deposit slip via email.
          </li>
          <li>
            Stationery charges and Annual charges are to be paid{" "}
            <strong>once a year</strong>, in the month of July and August respectively.
          </li>
          {netTuitionFeeAmount != null && (
            <li>
              Your child's Tuition fee will be{" "}
              <strong>Rs. {Math.round(netTuitionFeeAmount).toLocaleString()}/-</strong> per month.
            </li>
          )}
          <li>
            Tuition fee and other charges will increase by <strong>10-12%</strong> every year.
          </li>
          <li>
            <strong>Rs. 1,000/- per month</strong> will be charged for STEAM Education program
            from Grade I-VIII.
          </li>
        </ul>
      </div>

      {/* Step 3: Required Device and Applications for Learning */}
      <div className="mb-3 p-3 bg-light rounded border">
        <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
          3. Required Device and Applications for Learning
        </h6>
        <p className="mb-2">
          A <strong>tablet or iPad with stylus support</strong> must be arranged for your
          child. Recommended devices and applications include:
        </p>
        <div className="row g-2 mb-2">
          <div className="col-md-6">
            <div className="p-2 bg-white rounded border h-100">
              <div className="fw-semibold text-dark mb-1 small text-uppercase">
                Recommended Devices:
              </div>
              <ul className="mb-0 ps-3 small text-secondary">
                <li>Samsung Galaxy Tab S6 Lite</li>
                <li>iPad (9th or 10th Generation)</li>
                <li>Samsung Galaxy Tab S9 FE</li>
              </ul>
            </div>
          </div>
          <div className="col-md-6">
            <div className="p-2 bg-white rounded border h-100">
              <div className="fw-semibold text-dark mb-1 small text-uppercase">
                Required Applications:
              </div>
              <ul className="mb-0 ps-3 small text-secondary">
                <li>Microsoft Office</li>
                <li>Adobe Acrobat Reader</li>
                <li>Raz Kids app</li>
                <li>Google Classroom</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="alert alert-warning py-1 px-2 mb-0 small text-dark">
          <strong>Note:</strong> The device must be kept in a <strong>protective cover</strong> to
          avoid damage. The school will not be responsible for any loss or damage.
        </div>
      </div>

      {/* Step 4: School Timings */}
      <div className="mb-3 p-3 bg-light rounded border">
        <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
          4. School Timings
        </h6>
        <div className="p-2 bg-white rounded border mb-2 small">
          <div className="row g-1">
            <div className="col-sm-6">
              <strong>First Day of School:</strong>
              <div>
                Date:{" "}
                <span className="text-decoration-underline fw-semibold">
                  ____________________
                </span>
              </div>
            </div>
            <div className="col-sm-6">
              <div>
                Timings: <strong>9:00 AM to 11:00 AM</strong>
              </div>
              <div>
                Regular Timings: <em>Please see the rule book.</em>
              </div>
            </div>
          </div>
        </div>
        <p className="small text-muted mb-0">
          Please read the rules and regulations booklet thoroughly to completely understand the
          school’s policies and procedures and to avoid any inconvenience.
        </p>
      </div>

      {/* Welcome & Closing */}
      <div className="text-center my-3">
        <p className="mb-0" style={{ fontSize: "14px" }}>
          We warmly welcome you to <strong>The Eon Academy</strong> and look forward to
          providing a nurturing and high-quality learning experience for your child.
        </p>
      </div>

      {/* Signature */}
      <div className="row mt-4 pt-2">
        <div className="col-12 text-end pe-5">
          <div style={{ display: "inline-block", textAlign: "center", minWidth: "200px" }}>
            <p className="mb-1">
              <strong>Sincerely,</strong>
            </p>
            <div style={{ height: "35px" }}></div>
            <p className="mb-0 fw-bold border-top pt-1 text-dark">
              Mrs. Hina Arsalan
            </p>
            <p className="mb-0 text-muted small fst-italic">
              Chief Executive
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EonAdmissionLetterContent;
